import { apiClient } from './apiClient';
import { errorCaptureService } from './errorCaptureService';
import type { ClientDiagnosticsHeartbeat, DeviceInfo, ServiceWorkerInfo } from '../types/diagnostics';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

class DiagnosticsService {
    private sessionId: string = '';
    private intervalId: ReturnType<typeof setInterval> | null = null;
    private running = false;

    // Composable pieces of the "where the user is" string. Setters debounce via
    // equality checks and POST the composed path when anything changes.
    private routePath: string = '';
    private dialogName: string | null = null;
    private contexts = new Map<string, string>();

    start(): void {
        if (this.running) return;
        this.running = true;
        this.sessionId = crypto.randomUUID();
        this.routePath = window.location.pathname + window.location.hash;

        this.sendHeartbeat();
        this.intervalId = setInterval(() => this.sendHeartbeat(), HEARTBEAT_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    stop(): void {
        this.running = false;
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.dialogName = null;
        this.contexts.clear();
    }

    private handleVisibilityChange = (): void => {
        if (document.visibilityState === 'visible' && this.running) {
            this.sendHeartbeat();
        }
    };

    setRoutePath(path: string): void {
        if (this.routePath === path) return;
        this.routePath = path;
        this.postCurrentPath();
    }

    setDialog(name: string | null): void {
        if (this.dialogName === name) return;
        this.dialogName = name;
        this.postCurrentPath();
    }

    setContext(key: string, label: string | null): void {
        if (label === null || label === '') {
            if (!this.contexts.has(key)) return;
            this.contexts.delete(key);
        } else {
            if (this.contexts.get(key) === label) return;
            this.contexts.set(key, label);
        }
        this.postCurrentPath();
    }

    private composePath(): string {
        let path = this.routePath;
        if (this.contexts.size > 0) {
            path += ` [${Array.from(this.contexts.values()).join(' | ')}]`;
        }
        if (this.dialogName) {
            path += ` [dialog: ${this.dialogName}]`;
        }
        return path;
    }

    private postCurrentPath(): void {
        if (!this.running) return;
        apiClient.post('/diagnostics/page', { currentPage: this.composePath() }).catch(() => {
            // Diagnostics must never break the app
        });
    }

    private async sendHeartbeat(): Promise<void> {
        try {
            const [deviceInfo, swInfo] = await Promise.all([
                this.gatherDeviceInfo(),
                this.gatherServiceWorkerInfo(),
            ]);

            const heartbeat: ClientDiagnosticsHeartbeat = {
                sessionId: this.sessionId,
                deviceInfo,
                serviceWorkerInfo: swInfo,
                appVersion: swInfo.cacheName,
                isOnline: navigator.onLine,
                connectionType: (navigator as any).connection?.effectiveType ?? null,
                currentPage: this.composePath(),
            };

            await apiClient.post('/diagnostics/heartbeat', heartbeat);

            const errors = errorCaptureService.flushErrors();
            if (errors.length > 0) {
                await apiClient.post('/diagnostics/errors', errors);
            }
        } catch {
            // Diagnostics must never break the app
        }
    }

    private gatherDeviceInfo(): DeviceInfo {
        return {
            userAgent: navigator.userAgent,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            devicePixelRatio: window.devicePixelRatio,
            platform: navigator.platform,
            language: navigator.language,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }

    private async gatherServiceWorkerInfo(): Promise<ServiceWorkerInfo> {
        const info: ServiceWorkerInfo = {
            cacheName: null,
            state: 'Not registered',
            hasWaitingWorker: false,
            cacheEntryCount: 0,
        };

        try {
            if ('serviceWorker' in navigator) {
                const reg = await navigator.serviceWorker.getRegistration();
                if (reg) {
                    if (reg.active) {
                        info.state = reg.active.state;
                    }
                    info.hasWaitingWorker = !!reg.waiting;
                }
            }

            if ('caches' in window) {
                const names = await caches.keys();
                const mindshareCache = names.find((n) => n.startsWith('mindshare-cache'));
                if (mindshareCache) {
                    info.cacheName = mindshareCache;
                    const cache = await caches.open(mindshareCache);
                    const keys = await cache.keys();
                    info.cacheEntryCount = keys.length;
                }
            }
        } catch {
            // SW/cache APIs may throw in certain contexts
        }

        return info;
    }
}

export const diagnosticsService = new DiagnosticsService();
